import React, { useMemo } from 'react';
import { ModalBlocker, ModalHeader, ModalWindow } from '@epam/promo';
import { useUuiContext, IModal } from '@epam/uui';
import {
  FlexCell,
  FlexRow,
  FlexSpacer,
  Button,
  LabeledInput,
  useForm,
  RadioGroup,
  Alert,
  Text,
} from '@epam/promo';
import { ReactComponent as NotificatioExclamationMark } from '@epam/assets/icons/common/notification-error-fill-24.svg';
import usePreventReloadOnFormChanged from 'component/hooks/use-prevent-reload';
import './styles.scss';
import useNotifications from 'hooks/useNotifications';
import { useBlocker } from 'hooks/useBlocker';
import { useCloseBlocker } from 'hooks/useCloseBlocker';
import { useLeaveConfirm } from 'hooks/useLeaveConfirm';
import { showErrorMessages } from 'component/errorMessagesRenderer/errorMessagesRenderer';
import { validationErrors } from 'models/errors';
import {
  IOpportunityReport,
  IOpportunityReportPayload,
} from '../../models/model';
import { reportSchema } from 'modules/project-list/components/generate-report/validationSchema';
import opportunityService from 'services/api/opportunity/opportunityService';

interface Props {
  modalProps: IModal<boolean>;
  reportParams?: IOpportunityReportPayload;
}

const GenerateReportModal = ({ modalProps, reportParams }: Props) => {
  const svc = useUuiContext();
  const { showSuccess } = useNotifications();
  const oppotunityReportPayload: IOpportunityReportPayload = {
    challengeId: reportParams?.challengeId,
    searchPhrase: reportParams?.searchPhrase,
    positions: reportParams?.positions,
    technologies: reportParams?.technologies,
    challenges: reportParams?.challenges,
    offset: {
      startPoint: reportParams?.offset?.startPoint,
      amount: reportParams?.offset?.amount,
    },
  };
  const { lens, save, isChanged } = useForm<IOpportunityReport>({
    value: {
      fileType: 'XLSX',
    },
    onSave: async () => {
      useBlocker(svc);
      try {
        await opportunityService.generateReport(
          lens.get().fileType,
          oppotunityReportPayload
        );
        showSuccess(
          'The report will be delivered to your email in 5-10 minutes'
        );
      } catch (errors: any) {
        return Promise.reject(errors);
      } finally {
        useCloseBlocker(svc);
      }
    },
    onError: (errors: validationErrors) => {
      showErrorMessages(svc, errors);
    },
    getMetadata: reportSchema,
    beforeLeave: null,
  });

  const showLeaveConfirmation = useLeaveConfirm(modalProps, isChanged);
  usePreventReloadOnFormChanged({ isChanged: !!isChanged });

  const isOtherModalActive = useMemo(
    () => svc.uuiModals?.getOperations()?.length > 1,
    [modalProps]
  );

  return (
    <ModalBlocker
      {...modalProps}
      abort={showLeaveConfirmation}
      cx="generate-report"
    >
      <ModalWindow>
        <div className="generate-report__modalHeader">
          <ModalHeader
            borderBottom
            onClose={() =>
              isOtherModalActive ? null : showLeaveConfirmation()
            }
          >
            Generate Report
          </ModalHeader>
        </div>
        <div className="generate-report__modalContent">
          {reportParams?.positions ||
          reportParams?.searchPhrase ||
          reportParams?.technologies ? (
            <Alert
              cx={'generate-report__notification'}
              color="blue"
              icon={NotificatioExclamationMark}
            >
              <Text size="30">
                This report will be generated based on currently applied
                filters.
              </Text>
            </Alert>
          ) : null}
          <FlexCell width="100%">
            <Text size="30">
              You're going to generate the Opportunities report.
            </Text>
            <FlexRow vPadding="12">
              <FlexCell grow={1}>
                <LabeledInput
                  label="File Type"
                  {...lens.prop('fileType').toProps()}
                >
                  <RadioGroup
                    cx="generate-report__radioGroup"
                    items={[
                      { id: 'XLS', name: '.xls' },
                      { id: 'XLSX', name: '.xlsx' },
                    ]}
                    {...lens.prop('fileType').toProps()}
                  ></RadioGroup>
                </LabeledInput>
              </FlexCell>
            </FlexRow>
            <FlexRow vPadding="12" cx="generate-report__buttons">
              <FlexSpacer />
              <Button
                cx="generate-report__cancelButton"
                caption="Cancel"
                fill="white"
                color="gray50"
                onClick={showLeaveConfirmation}
              />
              <Button color="blue" caption="Generate Report" onClick={save} />
            </FlexRow>
          </FlexCell>
        </div>
      </ModalWindow>
    </ModalBlocker>
  );
};

export default GenerateReportModal;
